import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Table, Button, Group, Text, Modal,
  NumberInput, Select, Stack, Loader, Center, ActionIcon,
  Tooltip, Badge, Textarea
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconPencil, IconTrash, IconArrowsExchange, IconCopy } from '@tabler/icons-react';
import 'dayjs/locale/it';

const CATEGORIE_ENTRATA = [
  { value: 'Trasferimenti', label: 'Trasferimenti' },
  { value: 'Altro entrata', label: 'Altro' },
];

const CATEGORIE_USCITA = [
  { value: 'Lavaggi', label: 'Lavaggi' },
  { value: 'Altro uscita', label: 'Altro' },
];

const EMPTY_FORM = {
  mese: null, sede: null, tipo: null, categoria: null, importo: 0, note: '',
};

export default function MovimentiUSave() {
  const [movimenti, setMovimenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/movimenti-usave');
      setMovimenti(res.data);
    } catch {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare i movimenti', color: 'red' });
    } finally { setLoading(false); }
  };

  const grouped = useMemo(() => {
    const map = {};
    movimenti.forEach(m => {
      const key = m.mese.substring(0, 7);
      if (!map[key]) map[key] = [];
      map[key].push(m);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [movimenti]);

  const resetForm = () => { setEditing(null); setForm({ ...EMPTY_FORM }); };

  const openDuplicate = (m) => {
    setEditing(null);
    setForm({
      mese: new Date(m.mese + 'T00:00:00'),
      sede: m.sede, tipo: m.tipo, categoria: m.categoria,
      importo: m.importo || 0, note: m.note || '',
    });
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      mese: new Date(m.mese + 'T00:00:00'),
      sede: m.sede, tipo: m.tipo, categoria: m.categoria,
      importo: m.importo || 0, note: m.note || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mese || !form.sede || !form.tipo || !form.categoria) {
      notifications.show({ title: 'Errore', message: 'Compila tutti i campi obbligatori', color: 'red' });
      return;
    }
    const d = form.mese;
    const meseStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const payload = {
      mese: meseStr, sede: form.sede, tipo: form.tipo,
      categoria: form.categoria, importo: form.importo || 0, note: form.note || null,
    };
    try {
      if (editing) {
        await api.put(`/movimenti-usave/${editing.id}`, payload);
        notifications.show({ title: 'Successo', message: 'Movimento aggiornato', color: 'green' });
      } else {
        await api.post('/movimenti-usave', payload);
        notifications.show({ title: 'Successo', message: 'Movimento registrato', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      await load();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore nel salvataggio', color: 'red' });
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Eliminare ${m.categoria} di ${formatMese(m.mese.substring(0, 7))}?`)) return;
    try {
      await api.delete(`/movimenti-usave/${m.id}`);
      notifications.show({ title: 'Successo', message: 'Movimento eliminato', color: 'green' });
      await load();
    } catch {
      notifications.show({ title: 'Errore', message: 'Errore eliminazione', color: 'red' });
    }
  };

  const fmt = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const formatMese = (key) => {
    const [y, m] = key.split('-');
    return new Date(+y, +m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  const categoriePerTipo = form.tipo === 'entrata' ? CATEGORIE_ENTRATA : form.tipo === 'uscita' ? CATEGORIE_USCITA : [];

  if (loading) return <Center h={400}><Loader size="lg" color="teal" /></Center>;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconArrowsExchange size={28} color="rgb(32, 83, 87)" />
          <Title order={2}>Entrate e Uscite Variabili</Title>
        </Group>
        <Button leftSection={<IconPlus size={18} />} onClick={() => { resetForm(); setShowModal(true); }}>
          Nuovo Movimento
        </Button>
      </Group>

      {grouped.length === 0 && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text ta="center" c="dimmed" py="xl">Nessun movimento registrato</Text>
        </Card>
      )}

      {grouped.map(([meseKey, rows]) => {
        const totEntrate = rows.filter(r => r.tipo === 'entrata').reduce((s, r) => s + r.importo, 0);
        const totUscite = rows.filter(r => r.tipo === 'uscita').reduce((s, r) => s + r.importo, 0);
        return (
          <Card key={meseKey} shadow="sm" padding={isMobile ? 'sm' : 'lg'} radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={700} size={isMobile ? 'md' : 'lg'} tt="capitalize">{formatMese(meseKey)}</Text>
              <Group gap="xs">
                <Badge color="green" variant="light" size={isMobile ? 'sm' : 'lg'}>+{fmt(totEntrate)}</Badge>
                <Badge color="red" variant="light" size={isMobile ? 'sm' : 'lg'}>-{fmt(totUscite)}</Badge>
              </Group>
            </Group>
            <Table striped highlightOnHover style={{ ...(isMobile ? { fontSize: '0.8rem' } : {}), tableLayout: 'fixed' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: isMobile ? '40%' : '20%' }}>Categoria</Table.Th>
                  {!isMobile && <Table.Th style={{ width: '15%' }}>Sede</Table.Th>}
                  <Table.Th ta="right" style={{ width: isMobile ? '35%' : '15%' }}>Importo</Table.Th>
                  {!isMobile && <Table.Th style={{ width: '35%' }}>Note</Table.Th>}
                  <Table.Th style={{ width: isMobile ? '25%' : '15%' }}>Azioni</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(m => (
                  <Table.Tr key={m.id} onClick={() => isMobile && openEdit(m)} style={isMobile ? { cursor: 'pointer' } : undefined}>
                    <Table.Td>
                      <Text size={isMobile ? 'xs' : 'sm'} fw={500}>{m.categoria}</Text>
                      {isMobile && (
                        <Group gap={4} mt={2}>
                          <Badge color={m.tipo === 'entrata' ? 'green' : 'red'} variant="light" size="xs">
                            {m.tipo === 'entrata' ? 'Entrata' : 'Uscita'}
                          </Badge>
                          <Badge variant="light" color={m.sede === 'FCO' ? 'blue' : 'orange'} size="xs">{m.sede}</Badge>
                        </Group>
                      )}
                    </Table.Td>
                    {!isMobile && (
                      <Table.Td>
                        <Badge variant="light" color={m.sede === 'FCO' ? 'blue' : 'orange'} size="sm">{m.sede}</Badge>
                      </Table.Td>
                    )}
                    <Table.Td ta="right">
                      <Text size={isMobile ? 'xs' : 'sm'} fw={600} c={m.tipo === 'entrata' ? 'green' : 'red'}>
                        {m.tipo === 'entrata' ? '+' : '-'}{fmt(m.importo)}
                      </Text>
                    </Table.Td>
                    {!isMobile && (
                      <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{m.note || '-'}</Text></Table.Td>
                    )}
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <ActionIcon variant="subtle" color="blue" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); openEdit(m); }}>
                          <IconPencil size={isMobile ? 14 : 16} />
                        </ActionIcon>
                        <Tooltip label="Duplica">
                          <ActionIcon variant="subtle" color="teal" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); openDuplicate(m); }}>
                            <IconCopy size={isMobile ? 14 : 16} />
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon variant="subtle" color="red" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleDelete(m); }}>
                          <IconTrash size={isMobile ? 14 : 16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        );
      })}

      <Modal
        opened={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editing ? 'Modifica Movimento' : 'Nuovo Movimento'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <MonthPickerInput
              label="Mese"
              locale="it"
              valueFormat="MMMM YYYY"
              value={form.mese}
              onChange={v => setForm({ ...form, mese: v })}
              required
            />
            <Select
              label="Sede"
              required
              data={[
                { value: 'FCO', label: 'FCO - Fiumicino' },
                { value: 'CIA', label: 'CIA - Ciampino' },
              ]}
              value={form.sede}
              onChange={v => setForm({ ...form, sede: v })}
            />
            <Select
              label="Tipo"
              required
              data={[
                { value: 'entrata', label: 'Entrata' },
                { value: 'uscita', label: 'Uscita' },
              ]}
              value={form.tipo}
              onChange={v => setForm({ ...form, tipo: v, categoria: null })}
            />
            <Select
              label="Categoria"
              required
              data={categoriePerTipo}
              value={form.categoria}
              onChange={v => setForm({ ...form, categoria: v })}
              disabled={!form.tipo}
              placeholder={form.tipo ? 'Seleziona categoria' : 'Seleziona prima il tipo'}
            />
            <NumberInput
              label="Importo"
              required
              decimalScale={2}
              prefix="€ "
              thousandSeparator="."
              decimalSeparator=","
              value={form.importo}
              onChange={v => setForm({ ...form, importo: v })}
            />
            <Textarea
              label="Note"
              rows={2}
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => { setShowModal(false); resetForm(); }}>Annulla</Button>
              <Button type="submit">Salva</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
